'use client';

import { useQuery } from 'convex-helpers/react/cache/hooks';
import { api } from 'convex/_generated/api';
import type { Id } from 'convex/_generated/dataModel';
import { useCreateHarvest, useDeleteHarvest, useUpdateHarvest } from '@api/harvest';
import { findGardenById, type Garden } from '@modules/my-gardens/business_logic';
import {
  buildHarvestDaySchedule,
  buildLast7DayDateRange,
  getHarvestPlantsForGarden,
  getLast7DayBounds,
  isPlantInGarden,
  measureUnitToFields,
  parseHarvestAmount,
  validateHarvestDraft,
  type HarvestDay,
  type MeasureUnit,
  type Plant,
} from '@modules/harvest/business_logic';
import { text as harvestText } from '@modules/harvest/text_business_logic';
import { useHarvestStore } from '@shared/store/useHarvestStore';
import { toISODate } from '@shared/dates';
import { ComponentType, type FormEvent, useEffect, useMemo } from 'react';

export interface HarvestProps {
  isLoading: boolean;
  isSaving: boolean;
  gardens: Garden[];
  selectedGarden: Garden | null;
  selectedGardenId: Id<'gardens'> | null;
  daySchedule: HarvestDay[];
  gardenPlants: Plant[];
  quickAddPlantId: string;
  quickAddAmount: string;
  quickAddMeasureUnit: MeasureUnit;
  quickAddDate: string;
  quickAddShowDate: boolean;
  editDialogOpen: boolean;
  editPlantId: string;
  editHarvestDate: string;
  editMeasureUnit: MeasureUnit;
  editAmount: string;
  deleteDialogOpen: boolean;
  onGardenChange: (id: Id<'gardens'> | null) => void;
  onQuickAddSubmit: (e: FormEvent) => void;
  onQuickAddPlantChange: (plantId: string) => void;
  onQuickAddAmountChange: (amount: string) => void;
  onQuickAddMeasureUnitChange: (unit: MeasureUnit) => void;
  onQuickAddDateChange: (date: string) => void;
  onToggleQuickAddShowDate: () => void;
  onStartEdit: (harvestId: Id<'harvests'>) => void;
  onEditSubmit: (e: FormEvent) => void;
  onEditCancel: () => void;
  onEditPlantChange: (plantId: string) => void;
  onEditHarvestDateChange: (date: string) => void;
  onEditMeasureUnitChange: (unit: MeasureUnit) => void;
  onEditAmountChange: (amount: string) => void;
  onConfirmDelete: (harvestId: Id<'harvests'>) => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
}

interface HarvestContainerProps {
  View: ComponentType<HarvestProps>;
  onError?: (message: string) => void;
}

function buildDraft(
  gardenId: string,
  plantId: string,
  harvestDate: string,
  measureUnit: MeasureUnit,
  amount: string
) {
  const { measureType, weightUnit } = measureUnitToFields(measureUnit);
  return {
    gardenId,
    plantId,
    harvestDate,
    measureType,
    amount,
    weightUnit,
  };
}

export function HarvestContainer({ View, onError }: HarvestContainerProps) {
  const gardensQuery = useQuery(api.gardens.queries.getUserGardens, {});
  const plantsQuery = useQuery(api.plants.queries.getAllPlants, {});

  const selectedGardenId = useHarvestStore((s) => s.selectedGardenId);
  const setSelectedGardenId = useHarvestStore((s) => s.setSelectedGardenId);
  const editDialogOpen = useHarvestStore((s) => s.editDialogOpen);
  const editingHarvestId = useHarvestStore((s) => s.editingHarvestId);
  const editPlantId = useHarvestStore((s) => s.editPlantId);
  const editHarvestDate = useHarvestStore((s) => s.editHarvestDate);
  const editMeasureUnit = useHarvestStore((s) => s.editMeasureUnit);
  const editAmount = useHarvestStore((s) => s.editAmount);
  const quickAddPlantId = useHarvestStore((s) => s.quickAddPlantId);
  const quickAddAmount = useHarvestStore((s) => s.quickAddAmount);
  const quickAddMeasureUnit = useHarvestStore((s) => s.quickAddMeasureUnit);
  const quickAddDate = useHarvestStore((s) => s.quickAddDate);
  const quickAddShowDate = useHarvestStore((s) => s.quickAddShowDate);
  const deleteDialogOpen = useHarvestStore((s) => s.deleteDialogOpen);
  const harvestToDelete = useHarvestStore((s) => s.harvestToDelete);
  const isSaving = useHarvestStore((s) => s.isSaving);

  const setIsSaving = useHarvestStore((s) => s.setIsSaving);
  const startEdit = useHarvestStore((s) => s.startEdit);
  const cancelEdit = useHarvestStore((s) => s.cancelEdit);
  const setEditPlantId = useHarvestStore((s) => s.setEditPlantId);
  const setEditHarvestDate = useHarvestStore((s) => s.setEditHarvestDate);
  const setEditMeasureUnit = useHarvestStore((s) => s.setEditMeasureUnit);
  const setEditAmount = useHarvestStore((s) => s.setEditAmount);
  const setQuickAddPlantId = useHarvestStore((s) => s.setQuickAddPlantId);
  const setQuickAddAmount = useHarvestStore((s) => s.setQuickAddAmount);
  const setQuickAddMeasureUnit = useHarvestStore((s) => s.setQuickAddMeasureUnit);
  const setQuickAddDate = useHarvestStore((s) => s.setQuickAddDate);
  const toggleQuickAddShowDate = useHarvestStore((s) => s.toggleQuickAddShowDate);
  const resetQuickAdd = useHarvestStore((s) => s.resetQuickAdd);
  const confirmDelete = useHarvestStore((s) => s.confirmDelete);
  const cancelDelete = useHarvestStore((s) => s.cancelDelete);

  const dateRange = useMemo(() => buildLast7DayDateRange(), []);
  const { startDate, endDate } = useMemo(() => getLast7DayBounds(dateRange), [dateRange]);

  const gardens = gardensQuery ?? [];
  const plants = plantsQuery ?? [];

  useEffect(() => {
    if (!selectedGardenId && gardens.length > 0) {
      setSelectedGardenId(gardens[0]!._id);
    }
  }, [selectedGardenId, gardens, setSelectedGardenId]);

  const selectedGarden = findGardenById(gardens, selectedGardenId);
  const gardenPlants = getHarvestPlantsForGarden(selectedGarden, plants);

  useEffect(() => {
    if (!selectedGarden) return;
    if (quickAddPlantId && !isPlantInGarden(selectedGarden, quickAddPlantId)) {
      setQuickAddPlantId('');
    }
    if (editPlantId && editDialogOpen && !isPlantInGarden(selectedGarden, editPlantId)) {
      setEditPlantId('');
    }
  }, [
    selectedGarden,
    quickAddPlantId,
    editPlantId,
    editDialogOpen,
    setQuickAddPlantId,
    setEditPlantId,
  ]);

  useEffect(() => {
    if (!selectedGarden || gardenPlants.length === 0) return;
    if (!quickAddPlantId && gardenPlants.length === 1) {
      setQuickAddPlantId(gardenPlants[0]!.id);
    }
  }, [selectedGarden, gardenPlants, quickAddPlantId, setQuickAddPlantId]);

  const harvestsQuery = useQuery(
    api.harvest.queries.getHarvestsForGarden,
    selectedGarden ? { gardenId: selectedGarden._id, startDate, endDate } : 'skip'
  );

  const harvestQueryArgs = selectedGarden
    ? { gardenId: selectedGarden._id, startDate, endDate }
    : null;

  const createHarvest = useCreateHarvest(
    harvestQueryArgs ?? { gardenId: '' as Id<'gardens'>, startDate, endDate }
  );
  const updateHarvest = useUpdateHarvest(
    harvestQueryArgs ?? { gardenId: '' as Id<'gardens'>, startDate, endDate }
  );
  const deleteHarvest = useDeleteHarvest(
    harvestQueryArgs ?? { gardenId: '' as Id<'gardens'>, startDate, endDate }
  );

  const harvests = harvestsQuery ?? [];
  const daySchedule = buildHarvestDaySchedule(harvests, plants, dateRange);

  const isLoading =
    gardensQuery === undefined ||
    plantsQuery === undefined ||
    (selectedGarden !== null && harvestsQuery === undefined);

  const submitHarvest = async (
    gardenId: Id<'gardens'>,
    plantId: string,
    harvestDate: string,
    measureUnit: MeasureUnit,
    amountStr: string,
    onSuccess: () => void
  ) => {
    const draft = buildDraft(gardenId, plantId, harvestDate, measureUnit, amountStr);
    const validationError = validateHarvestDraft(draft);
    if (validationError) {
      onError?.(harvestText.harvestValidationErrorMessage(validationError));
      return;
    }

    const amount = parseHarvestAmount(draft);
    const { measureType, weightUnit } = measureUnitToFields(measureUnit);

    setIsSaving(true);
    try {
      await createHarvest({
        gardenId,
        plantId,
        harvestDate,
        measureType,
        amount,
        weightUnit: measureType === 'weight' ? weightUnit : undefined,
      });
      onSuccess();
    } catch (error) {
      onError?.(harvestText.harvestMutationErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleQuickAddSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedGarden) return;

    const harvestDate = quickAddShowDate ? quickAddDate : toISODate(new Date());

    await submitHarvest(
      selectedGarden._id,
      quickAddPlantId,
      harvestDate,
      quickAddMeasureUnit,
      quickAddAmount,
      resetQuickAdd
    );
  };

  const handleEditSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedGarden || !editingHarvestId) return;

    const draft = buildDraft(
      selectedGarden._id,
      editPlantId,
      editHarvestDate,
      editMeasureUnit,
      editAmount
    );
    const validationError = validateHarvestDraft(draft);
    if (validationError) {
      onError?.(harvestText.harvestValidationErrorMessage(validationError));
      return;
    }

    const amount = parseHarvestAmount(draft);
    const { measureType, weightUnit } = measureUnitToFields(editMeasureUnit);

    setIsSaving(true);
    try {
      await updateHarvest({
        harvestId: editingHarvestId,
        gardenId: selectedGarden._id,
        plantId: editPlantId,
        harvestDate: editHarvestDate,
        measureType,
        amount,
        weightUnit: measureType === 'weight' ? weightUnit : undefined,
      });
      cancelEdit();
    } catch (error) {
      onError?.(harvestText.harvestMutationErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEdit = (harvestId: Id<'harvests'>) => {
    const harvest = harvests.find((h) => h._id === harvestId);
    if (!harvest) return;
    startEdit({
      ...harvest,
      measureType: harvest.measureType,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!harvestToDelete) return;

    setIsSaving(true);
    try {
      await deleteHarvest({ harvestId: harvestToDelete });
      cancelDelete();
    } catch (error) {
      onError?.(harvestText.harvestMutationErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <View
      isLoading={isLoading}
      isSaving={isSaving}
      gardens={gardens}
      selectedGarden={selectedGarden}
      selectedGardenId={selectedGardenId}
      daySchedule={daySchedule}
      gardenPlants={gardenPlants}
      quickAddPlantId={quickAddPlantId}
      quickAddAmount={quickAddAmount}
      quickAddMeasureUnit={quickAddMeasureUnit}
      quickAddDate={quickAddDate}
      quickAddShowDate={quickAddShowDate}
      editDialogOpen={editDialogOpen}
      editPlantId={editPlantId}
      editHarvestDate={editHarvestDate}
      editMeasureUnit={editMeasureUnit}
      editAmount={editAmount}
      deleteDialogOpen={deleteDialogOpen}
      onGardenChange={setSelectedGardenId}
      onQuickAddSubmit={handleQuickAddSubmit}
      onQuickAddPlantChange={setQuickAddPlantId}
      onQuickAddAmountChange={setQuickAddAmount}
      onQuickAddMeasureUnitChange={setQuickAddMeasureUnit}
      onQuickAddDateChange={setQuickAddDate}
      onToggleQuickAddShowDate={toggleQuickAddShowDate}
      onStartEdit={handleStartEdit}
      onEditSubmit={handleEditSubmit}
      onEditCancel={cancelEdit}
      onEditPlantChange={setEditPlantId}
      onEditHarvestDateChange={setEditHarvestDate}
      onEditMeasureUnitChange={setEditMeasureUnit}
      onEditAmountChange={setEditAmount}
      onConfirmDelete={confirmDelete}
      onDeleteConfirm={handleDeleteConfirm}
      onDeleteCancel={cancelDelete}
    />
  );
}
